/**
 * useKiaanColumns — which side columns of the Kiaan tab are open (SUN-56, Manak 2026-10-19).
 * `true` = open. Remembered per person in localStorage['kiaan.widget.columns.v1.<userId>'];
 * default both open. Storage blocked or junk → both open; the widget never breaks.
 */
import { useCallback, useState } from 'react';
import { useAuthStore } from '../../stores/auth';

export interface KiaanColumns {
  left: boolean;
  right: boolean;
}

const OPEN: KiaanColumns = { left: true, right: true };

export const columnsKey = (userId: string) => `kiaan.widget.columns.v1.${userId}`;

function readColumns(userId: string | null): KiaanColumns {
  if (!userId) return OPEN;
  try {
    const v = JSON.parse(window.localStorage.getItem(columnsKey(userId)) ?? 'null');
    if (v && typeof v.left === 'boolean' && typeof v.right === 'boolean') return { left: v.left, right: v.right };
  } catch {
    /* blocked or junk → default */
  }
  return OPEN;
}

function writeColumns(userId: string | null, cols: KiaanColumns) {
  if (!userId) return;
  try {
    window.localStorage.setItem(columnsKey(userId), JSON.stringify(cols));
  } catch {
    /* storage blocked — the choice just is not remembered */
  }
}

export function useKiaanColumns(): [KiaanColumns, (side: keyof KiaanColumns) => void] {
  const userId: string | null = useAuthStore((s) => s.user?.id ?? null);
  const [state, setState] = useState(() => ({ userId, cols: readColumns(userId) }));
  // The user loads after the widget, or someone else signs in: read their own choice.
  const current = state.userId === userId ? state : { userId, cols: readColumns(userId) };
  if (current !== state) setState(current);

  const toggle = useCallback(
    (side: keyof KiaanColumns) => {
      setState((s) => {
        const cols = { ...s.cols, [side]: !s.cols[side] };
        writeColumns(s.userId, cols);
        return { userId: s.userId, cols };
      });
    },
    [],
  );
  return [current.cols, toggle];
}
